/**
 * Files behind the API need the bearer token, so they are fetched and opened
 * as blobs rather than linked directly.
 */
import { getAccessToken } from './api';

async function authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getAccessToken();
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: 'include',
    headers: { ...(init.headers ?? {}), ...(token ? { authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    const text = await response.text();
    let message = `Request failed (${response.status})`;
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? message;
    } catch {
      // not JSON
    }
    throw new Error(message);
  }
  return response;
}

/** Opens a PDF (or any file) from the API in a new tab. */
export async function openFile(path: string): Promise<void> {
  // Opened first, synchronously, so pop-up blockers allow it.
  const tab = window.open('', '_blank');
  try {
    const blob = await (await authorizedFetch(path)).blob();
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

export interface Uploaded {
  id: string;
  filename: string;
  size: number;
  sha256: string;
}

export async function uploadFile(file: File, entity: string, entityId?: string): Promise<Uploaded> {
  const form = new FormData();
  form.append('file', file);
  const qs = new URLSearchParams({ entity, ...(entityId ? { entityId } : {}) });
  const response = await authorizedFetch(`/files?${qs.toString()}`, { method: 'POST', body: form });
  return (await response.json()) as Uploaded;
}

/** Downloads a file from the API with the name the server gives it. */
export async function downloadFile(path: string): Promise<void> {
  const response = await authorizedFetch(path);
  const name = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? 'download';
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Posts a multipart form (e.g. an import file) and returns the JSON reply. */
export async function postForm<T>(path: string, form: FormData): Promise<T> {
  return (await (await authorizedFetch(path, { method: 'POST', body: form })).json()) as T;
}
